import {Component, OnInit} from '@angular/core';
import {OfferAttributeType} from "@core/shared/models/offer-attribute-type";
import {LOCALE_ITEMS, LocaleItem, TranslationBuilder} from "@core/shared/models/translation";
import {MatSnackBar} from "@angular/material/snack-bar";
import {ActivatedRoute, Router} from "@angular/router";
import {FormControl, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {TranslateService} from "@ngx-translate/core";
import {OfferAttributeService} from "@core/shared/services/offer-attribute.service";
import {FormService} from "@core/shared/services/form.service";
import {TranslationService} from "@core/shared/services/translation.service";
import {CrudService} from "@core/shared/services/crud.service";
import {OfferAttributeTypeService} from "@core/shared/services/offer-attribute-type.service";
import {OfferAttribute, OfferAttributeTranslation} from "@core/shared/models/offer-attribute";
import {ArrayFilterField} from "@core/shared/models/filter/array-filter-field";

@Component({
    selector: 'app-core-offer-attribute-form',
    templateUrl: './offer-attribute-form.component.html',
    styleUrl: './offer-attribute-form.component.scss',
    providers: [
        FormService
    ],
    standalone: false
})
export class OfferAttributeFormComponent implements OnInit {

    public offerAttribute: OfferAttribute;

    public offerAttributeTypes: OfferAttributeType[] = [];

    public translationBuilder: TranslationBuilder;

    public parentOfferAttributes: OfferAttribute[] = [];

    public childOfferAttributes: OfferAttribute[] = [];

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _snackBar: MatSnackBar,
        private _router: Router,
        private _formBuilder: UntypedFormBuilder,
        private _translateService: TranslateService,
        private _offerAttributeService: OfferAttributeService,
        private _offerAttributeTypeService: OfferAttributeTypeService,
        public crudService: CrudService,
        public formService: FormService,
        public translationService: TranslationService
    ) {
    }

    ngOnInit(): void {

        this.offerAttribute = this.crudService.isUpdateAction ? (this._activatedRoute.snapshot.data as { offerAttribute: OfferAttribute }).offerAttribute : null;

        this._loadOfferAttributeTypes();

        this._initForm();

        this._initTranslationsForm();

        this._hydrateForm();

        this._initEvents();
    }

    private _initTranslationsForm(): void {

        this.translationBuilder = new TranslationBuilder(this._formBuilder);

        this.translationBuilder.form = this.form;

        this.translationBuilder.addItemCallback = (): UntypedFormGroup => {

            return this._formBuilder.group({
                label: ['', [Validators.required]]
            });
        };

        if(this.crudService.isUpdateAction){

            return;
        }

        LOCALE_ITEMS.forEach((localeItem: LocaleItem): void => {

            this.translationBuilder.addItemControl(localeItem);
        });
    }

    private _initParentOfferAttributes(): void {

        if(this.currentOfferAttributeType && this.currentOfferAttributeType.allowedParent){

            const typeFilter: ArrayFilterField = new ArrayFilterField('type.tag', 'andin', this.currentOfferAttributeType.allowedParent.tag);

            const params: string[] = [
                typeFilter.serialize,
                'sort[translations.label]=asc'
            ];

            this._offerAttributeService.getItemsAPI(params).subscribe((items: OfferAttribute[]): void => {

                this.parentOfferAttributes = items;
            });
        }
        else{

            this.parentOfferAttributes = [];
        }
    }

    private _initChildOfferAttributes(): void {

        if(this.currentOfferAttributeType && this.currentOfferAttributeType.allowedChild){

            const typeFilter: ArrayFilterField = new ArrayFilterField('type.tag', 'andin', this.currentOfferAttributeType.allowedChild.tag);

            const params: string[] = [
                typeFilter.serialize,
                'sort[translations.label]=asc'
            ];

            this._offerAttributeService.getItemsAPI(params).subscribe((items: OfferAttribute[]): void => {

                this.childOfferAttributes = items;
            });
        }
        else{

            this.childOfferAttributes = [];
        }
    }

    private _handleReference(): void {

        if(this.currentOfferAttributeType.requiredReference){

            this.form.get('reference').setValidators([Validators.required]);

        } else {

            this.form.get('reference').patchValue(null);

            this.form.get('reference').clearValidators();

            this.form.get('reference').updateValueAndValidity();
        }
    }

    private _loadOfferAttributeTypes(): void {

        this._offerAttributeTypeService.getItemsAPI().subscribe((items: OfferAttributeType[]): void => {

            this.offerAttributeTypes = items;

            if(this.crudService.isCreateAction){

                return;
            }

            this._initParentOfferAttributes();

            this._initChildOfferAttributes();

            this._handleReference();
        });
    }

    private _initForm(): void {

        this.formService.form = this._formBuilder.group({
            translations: new UntypedFormArray([]),
            type: this._formBuilder.group({
                id: [null, Validators.required]
            }),
            parent: [null],
            children: [[]],
            reference: [null]
        });

        this.formService.submitCallback = (): void => {

            if (this.crudService.isCreateAction) {

                this._offerAttributeService.createItemAPI(this.form.getRawValue()).subscribe((): void => {

                    this._snackBar.open(this._translateService.instant('offer.attribute.create.success.value'), this._translateService.instant('notification.close.action.value'), {
                        duration: 5000
                    });

                    this.redirectToList();
                });
            }

            if (this.crudService.isUpdateAction) {

                this._offerAttributeService.updateItemAPI(this.offerAttribute.id, this.form.getRawValue()).subscribe((): void => {

                    this._snackBar.open(this._translateService.instant('offer.attribute.update.success.value'), this._translateService.instant('notification.close.action.value'), {
                        duration: 5000
                    });

                    this.redirectToList();
                });
            }
        }
    }

    private _hydrateForm(): void {

        if (this.crudService.isCreateAction) {

            return;
        }

        this.form.patchValue(Object.assign({...this.offerAttribute}, {}));

        this.offerAttribute.translations.forEach((item: OfferAttributeTranslation): void => {

            const control: FormGroup = this.translationBuilder.addItemControl(this.translationBuilder.getLocaleItem(item.locale));

            control.patchValue({
                label: item.label
            });

            if('id' in item){

                control.addControl('id', new FormControl<number>(item.id, [Validators.required]));
            }
        });

        this.form.get('type').disable();
    }

    private _initEvents(): void {

        this.form.get('type').get('id').valueChanges.subscribe((): void => {

            this.form.get('parent').patchValue(null);

            this.form.get('children').patchValue([]);

            this._initParentOfferAttributes();

            this._initChildOfferAttributes();

            this._handleReference();
        });
    }

    public redirectToList(): void {

        this._router.navigate(['account/offer/attribute/list']);
    }

    public compareById(a: { id: number }, b: { id: number }): boolean {

        if(!a || !b){

            return false;
        }

        return a.id === b.id;
    }

    get form(): UntypedFormGroup {

        return this.formService.form;
    }

    get typeForm(): UntypedFormGroup {

        return this.form.get('type') as UntypedFormGroup;
    }

    get localeId(): string {

        return this._translateService.currentLang;
    }

    get locales(): LocaleItem[] {

        return LOCALE_ITEMS;
    }

    get currentOfferAttributeType(): OfferAttributeType {

        const control: FormGroup = this.form.get('type') as FormGroup;

        if(!control.get('id').value){

            return null;
        }

        return this.offerAttributeTypes.find((item: OfferAttributeType): boolean => {

            return item.id === control.get('id').value;
        });
    }
}
